import { useCallback, useEffect, useRef, useState } from "react";

import type { ChatPopoutMode } from "~/ai-chat/feature/use-chat-popout-mode";
import { showToast } from "~/components/__deprecated";

interface DocumentPictureInPictureApi {
  requestWindow: (options?: { width?: number; height?: number }) => Promise<Window>;
}

// Same-origin host page for "Standard window" mode. Opening a real URL (not "") keeps the browser's
// address bar showing this path instead of "about:blank" (which reads as broken). Served from
// `public/`, so it lives at the site root — the folder name never appears in the URL.
const HOST_PAGE_PATH = "/istari-ai-chat.html";

// Wait for the host page to finish loading before portaling into its body. Polls location + readyState
// so we don't act on the brief about:blank placeholder a fresh window shows before its URL loads.
function waitForHostPage(win: Window): Promise<boolean> {
  const deadlineMs = 3000;
  const start = performance.now();
  return new Promise((resolve) => {
    const tick = () => {
      let ready = false;
      try {
        ready =
          win.location.href.includes(HOST_PAGE_PATH) && win.document.readyState === "complete" && !!win.document.body;
      } catch {
        ready = false; // cross-origin during navigation — keep waiting
      }
      if (ready) return resolve(true);
      if (win.closed || performance.now() - start > deadlineMs) return resolve(false);
      setTimeout(tick, 30);
    };
    tick();
  });
}

// Document Picture-in-Picture (Chromium) gives a browser-managed, always-on-top window — it floats
// above the app and stays visible. Not in the DOM lib types yet, so read it off `window` defensively.
function getDocumentPiP(): DocumentPictureInPictureApi | undefined {
  return (window as unknown as { documentPictureInPicture?: DocumentPictureInPictureApi }).documentPictureInPicture;
}

const MIRRORED_STYLE_ATTRIBUTE = "data-istari-popout-style";

// Keep the external window's styles and theme synchronized for its full lifetime. This covers runtime
// theme changes, lazy-loaded stylesheets, and Vite HMR without copying scripts or unrelated head nodes.
export function synchronizeExternalWindowStyles(target: Window): () => void {
  const doc = target.document;
  const syncStyles = () => {
    doc.head.querySelectorAll(`[${MIRRORED_STYLE_ATTRIBUTE}]`).forEach((node) => node.remove());
    for (const node of document.querySelectorAll('style, link[rel="stylesheet"]')) {
      const clone = node.cloneNode(true) as Element;
      clone.setAttribute(MIRRORED_STYLE_ATTRIBUTE, "");
      doc.head.appendChild(clone);
    }
  };
  const syncTheme = () => {
    doc.documentElement.className = document.documentElement.className;
  };

  syncStyles();
  syncTheme();
  doc.title = "Istari AI";
  doc.body.style.margin = "0";
  doc.body.style.height = "100vh";
  doc.body.classList.add("bg-background", "text-foreground");

  const headObserver = new MutationObserver(syncStyles);
  headObserver.observe(document.head, { attributes: true, characterData: true, childList: true, subtree: true });
  const themeObserver = new MutationObserver(syncTheme);
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

  return () => {
    headObserver.disconnect();
    themeObserver.disconnect();
  };
}

interface Size {
  width: number;
  height: number;
}

export function useChatPopout(size: Size, mode: ChatPopoutMode = "floating") {
  const [popoutWindow, setPopoutWindow] = useState<Window | null>(null);
  const windowRef = useRef<Window | null>(null);
  const openingRef = useRef(false);
  const mountedRef = useRef(true);
  const stopStyleSyncRef = useRef<(() => void) | null>(null);

  // Close the external window if the chat unmounts (e.g. the user closes the chat entirely). The
  // mounted flag also lets an in-flight `requestWindow()` bail without touching state after teardown.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopStyleSyncRef.current?.();
      stopStyleSyncRef.current = null;
      windowRef.current?.close();
      windowRef.current = null;
    };
  }, []);

  const dock = useCallback(() => {
    stopStyleSyncRef.current?.();
    stopStyleSyncRef.current = null;
    windowRef.current?.close();
    windowRef.current = null;
    setPopoutWindow(null);
  }, []);

  const popOut = useCallback(async () => {
    // Guard against rapid re-clicks: `requestWindow()` is async, so `windowRef` stays null while it's
    // pending — without this, a second click would start a second window.
    if (openingRef.current || windowRef.current) return;
    openingRef.current = true;
    try {
      const width = Math.round(size.width);
      const height = Math.round(size.height);
      let win: Window | null = null;
      // "floating" prefers Document Picture-in-Picture (always-on-top over every app). "window" skips
      // PiP entirely for a standard browser window that follows normal focus/z-order. Both fall back to
      // window.open when PiP is unavailable or rejects.
      const pip = mode === "floating" ? getDocumentPiP() : undefined;
      if (pip) {
        try {
          win = await pip.requestWindow({ width, height });
        } catch {
          win = null;
        }
      }
      // No PiP (either "window" mode or PiP unavailable/failed): open a standard window on the host page
      // and wait for it to load, so we portal into a real same-origin document, not "about:blank".
      if (!win) {
        win = window.open(HOST_PAGE_PATH, "istari-ai-chat", `popup,width=${width},height=${height}`);
        if (win && !(await waitForHostPage(win))) {
          win.close();
          win = null;
        }
      }
      if (!win) {
        showToast("Couldn't open the chat window — allow pop-ups for this site and try again.", "error");
        return;
      }
      // Unmounted (or docked) while the window was opening — don't adopt an orphaned window.
      if (!mountedRef.current) {
        win.close();
        return;
      }
      stopStyleSyncRef.current = synchronizeExternalWindowStyles(win);
      const owned = win;
      owned.addEventListener("pagehide", () => {
        // Dock and component unmount are the primary cleanup paths. pagehide also handles the user
        // closing the external window with browser or OS chrome, where React receives no click event.
        // Only clear state if this is still the current window — an older window's late `pagehide`
        // must not stomp a newer one.
        if (windowRef.current === owned) {
          stopStyleSyncRef.current?.();
          stopStyleSyncRef.current = null;
          windowRef.current = null;
          setPopoutWindow(null);
        }
      });
      windowRef.current = owned;
      setPopoutWindow(owned);
    } finally {
      openingRef.current = false;
    }
  }, [size.width, size.height, mode]);

  const toggle = useCallback(() => {
    if (windowRef.current) {
      dock();
    } else if (!openingRef.current) {
      void popOut();
    }
  }, [dock, popOut]);

  return { popoutWindow, isPoppedOut: popoutWindow !== null, toggle };
}
